import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import IconButton from '@mui/material/IconButton'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import BigModal from '../../../components/Modals/BigModal'
import AnDateField from '../../../components/DateTime/DateField'
import DocumentsField from '../../../helpers/DocumentsField'
import { uploadDocument } from '../../../helpers/uploadDocument'
import { GlobalContext } from '../../../App'
import { errorMessage, isOk, recordPracticalSignoff } from '../../helpers/trainingApi'

// Records an assessor's practical sign-off against a pathway's checklist.
// `template` is one entry of `signoffTemplates` from the employee e-learning
// response. As with training records, a failed evidence upload (demo tenants
// refuse S3 writes) still saves the sign-off, and a toast says so.
const PracticalSignoffDialog = ({ open, onClose, employeeId, employeeName, template, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [answers, setAnswers] = useState({})
  const [date, setDate] = useState(() => moment())
  const [notes, setNotes] = useState('')
  const [files, setFiles] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    setAnswers({})
    setDate(moment())
    setNotes('')
    setFiles([])
    setError(null)
  }, [open, template])

  const items = template?.items ?? []
  const allAnswered = items.every((item) => answers[item.id] !== undefined)
  const allMet = allAnswered && items.every((item) => answers[item.id] === true)
  const dateValid = !!date && date.isValid?.() && !date.isAfter(moment(), 'day')
  const canSubmit = allAnswered && dateValid && !saving

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      let evidence
      if (files[0]) {
        try {
          evidence = await uploadDocument(files[0], 'employees/mandatorytraining')
        } catch (err) {
          setToast({
            message: `${err.message} The sign-off has been saved without the evidence file.`,
            severity: 'warning',
          })
        }
      }
      // A sign-off dated today is stamped now; an earlier day at midday so
      // the date reads the same in any timezone.
      const assessedAt = date.isSame(moment(), 'day')
        ? moment().toISOString()
        : moment(date).hour(12).minute(0).second(0).toISOString()
      const res = await recordPracticalSignoff({
        employeeId,
        courseId: template.courseId,
        pathwayId: template.pathwayId,
        checklist: items.map((item) => ({ itemId: item.id, met: answers[item.id] === true })),
        assessedAt,
        notes: notes.trim() || undefined,
        evidence,
      })
      if (isOk(res)) onSaved?.(res.data)
      else setError(errorMessage(res, 'Unable to save this sign-off. Please try again.'))
    } catch (e) {
      console.error(e)
      setError('Unable to save this sign-off. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <BigModal
      open={open}
      onClose={saving ? undefined : onClose}
      autoWidth
      style={{ width: { xs: 'calc(100vw - 24px)', sm: 560 }, padding: 3 }}
    >
      <Stack gap={2} component="form" onSubmit={handleSubmit}>
        <Stack direction="row" justifyContent="space-between" alignItems="start">
          <Stack>
            <Typography variant="title3" fontWeight={700}>
              {template?.title || 'Record practical sign-off'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[employeeName, template?.courseTitle, template?.pathwayTitle].filter(Boolean).join(' · ')}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="Close" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {template?.description && <Typography variant="body2">{template.description}</Typography>}

        <Stack gap={1.5}>
          {items.map((item) => (
            <FormControl key={item.id} required>
              <FormLabel id={`signoff-${item.id}`} sx={{ color: 'text.primary', fontSize: '0.9rem' }}>
                {item.text}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby={`signoff-${item.id}`}
                value={answers[item.id] === undefined ? '' : answers[item.id] ? 'met' : 'notMet'}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [item.id]: e.target.value === 'met' }))}
              >
                <FormControlLabel value="met" control={<Radio size="small" />} label="Met" />
                <FormControlLabel value="notMet" control={<Radio size="small" />} label="Not met" />
              </RadioGroup>
            </FormControl>
          ))}
        </Stack>

        {allAnswered && !allMet && (
          <Alert severity="warning">
            Not every item is met. The sign-off will be recorded as not passed, and can be repeated
            later.
          </Alert>
        )}

        <AnDateField
          date={date}
          onChange={(value) => setDate(value)}
          label="Date of sign-off"
          required
          disableFuture
          fullWidth
        />

        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          inputProps={{ maxLength: 4000 }}
        />

        <Stack gap={1}>
          <Typography variant="body2" fontWeight={600}>
            Evidence
          </Typography>
          <DocumentsField
            files={files}
            onFilesChange={setFiles}
            single
            accept="application/pdf,image/*"
            caption="Optional. PDF or image of the completed assessment sheet, up to 10 MB"
            acceptLabel="PDF or image files"
            maxSizeMB={10}
            disabled={saving}
          />
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="success" disabled={!canSubmit}>
            {saving ? 'Saving...' : 'Record sign-off'}
          </Button>
        </Stack>
      </Stack>
    </BigModal>
  )
}

export default PracticalSignoffDialog
